import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const CategoryList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [masterCategory, setMasterCategory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} masterCategory`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  const getMasterCategory = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching masterCategory with params:", requestParams);
        const response = await masterService.getMasterCategory(requestParams);
        console.log("Category API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const categoryData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Category data:", categoryData);
          console.log("Pagination data:", paginationData);

          // Ensure categoryData is an array
          if (Array.isArray(categoryData)) {
            setMasterCategory(categoryData);
          } else {
            console.error("Category data is not an array:", categoryData);
            setMasterCategory([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch masterCategory" });
          setMasterCategory([]);
        }
      } catch (error) {
        console.error("Error fetching masterCategory:", error);
        notify.error({ message: "Failed to fetch masterCategory" });
        setMasterCategory([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  // Load masterCategory data
  useEffect(() => {
    setPageTitle("Category");
    getMasterCategory();
  }, [getMasterCategory, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getMasterCategory({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getMasterCategory({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getMasterCategory({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add genre
  const handleAddCategory = () => {
    navigate("/categories/add/new");
  };

  // Handle edit genre
  const handleEditCategory = (categoryId) => {
    navigate(`/categories/edit/${categoryId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Categories",
      dataIndex: "vc_name",
      key: "vc_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => {
        const nameA = a.vc_name || '';
        const nameB = b.vc_name || '';
        return nameA.localeCompare(nameB);
      },
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },

    {
      title: "Preference Order Number",
      dataIndex: "order",
      key: "order",

      render: (status) => (
        <Text strong style={{ fontSize: "14px" }}>
          {status}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "vc_is_active",
      key: "vc_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCategory(record.vc_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Categories ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="All"
              style={{ width: 140 }}
            >
              <Option value="all">All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            <Search
              placeholder="Search categories..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              size="middle"
            >
              Add Category
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={masterCategory}
          rowKey="vc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No masterCategory found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default CategoryList;
